import { Space } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { PermissionSetType } from 'api/models';
import { View } from 'api/views/types';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { Radio } from 'components/atoms/radio';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { toastService } from 'components/molecules/toast';
import { PROJECT_LIST_VIEW_QUERY_KEY } from 'constant';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { CreatableView } from '../types';

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface Props<ViewDefinition = any> {
  visible: boolean;
  currentViewDefinition: ViewDefinition;
  onClose: () => void;
  view?: View<ViewDefinition>;
  onViewUpdate?: (view: View<ViewDefinition>) => Promise<View<ViewDefinition>>;
  onViewCreate?: (
    view: CreatableView<ViewDefinition>,
  ) => Promise<View<ViewDefinition>>;
}
export const ManageViewModal: FC<Props> = ({
  visible,
  onClose,
  view,
  currentViewDefinition,
  onViewUpdate,
  onViewCreate,
}) => {
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;
  const isSuperUser =
    currentUserPermissionSetType === PermissionSetType.SuperUser;
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [form] = Form.useForm();
  const editMode = view != null;
  const queryClient = useQueryClient();

  const onFormValueChange = useCallback((_, allValues) => {
    setSaveDisabled(isEmpty(allValues.viewName));
  }, []);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(view ?? { viewName: '', self: true });
      setSaveDisabled(isEmpty(view?.viewName));
    } else {
      form.resetFields();
    }
  }, [visible, form, view]);

  const translate = useTranslate();

  const onSave = useCallback(() => {
    const formValues = form.getFieldsValue();

    setActionInProgress(true);
    if (editMode) {
      onViewUpdate?.({ ...view, ...formValues } as View<any>)
        .then(() => {
          toastService({
            title: translate('success'),
            message: translate('views.update.success', {
              viewName: formValues.viewName,
            }),
          }).success();
          queryClient.invalidateQueries(PROJECT_LIST_VIEW_QUERY_KEY);
          onClose();
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate('views.update.failed', {
              viewName: formValues.viewName,
            }),
          }).error();
        })
        .finally(() => setActionInProgress(false));
    } else {
      onViewCreate?.({
        self: true,
        ...formValues,
        viewDefinition: currentViewDefinition,
      })
        .then(() => {
          toastService({
            title: translate('success'),
            message: translate('views.create.success', {
              viewName: formValues.viewName,
            }),
          }).success();
          queryClient.invalidateQueries(PROJECT_LIST_VIEW_QUERY_KEY);
          onClose();
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate('views.create.failed', {
              viewName: formValues.viewName,
            }),
          }).error();
        })
        .finally(() => setActionInProgress(false));
    }
  }, [
    form,
    editMode,
    onViewUpdate,
    view,
    translate,
    onClose,
    onViewCreate,
    currentViewDefinition,
    queryClient,
  ]);

  return (
    <Modal
      title={
        editMode ? (
          <FormattedMessage id="views.edit_view.title" />
        ) : (
          <FormattedMessage id="views.new_view.title" />
        )
      }
      visible={visible}
      destroyOnClose
      footer={
        <Footer>
          <Button type="text" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            data-cy="views.new-view.save"
            type="primary"
            disabled={saveDisabled}
            onClick={onSave}
            loading={actionInProgress}
          >
            {editMode ? (
              <FormattedMessage id="update" />
            ) : (
              <FormattedMessage id="save" />
            )}
          </Button>
        </Footer>
      }
      onCancel={onClose}
    >
      <Form layout="vertical" onValuesChange={onFormValueChange} form={form}>
        <Form.Item
          label={<FormattedMessage id="views.view_name.label" />}
          name="viewName"
        >
          <Input
            data-cy="views.new-view.name"
            placeholder={useTranslation('views.new_view_name.placeholder')}
            autoFocus
            autoComplete="off"
          />
        </Form.Item>
        {isAccountAdmin || isSuperUser ? (
          <Form.Item
            label={<FormattedMessage id="views.view_available.label" />}
            name="self"
          >
            <RadioGroup>
              <Space direction="vertical">
                <Radio value>
                  <FormattedMessage id="views.view_available.options.me" />
                </Radio>
                <Radio
                  data-cy="views.new-view.available.options.team"
                  value={false}
                >
                  <FormattedMessage id="views.view_available.options.team" />
                </Radio>
              </Space>
            </RadioGroup>
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
};
